import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectIsAuth, selectProfile } from '@store/profile.reducer'

const SCRIPT_ID = 'hde-chat-widget'
const SCRIPT_SRC = '//cdn5.helpdeskeddy.com//js/modules/chat_widget/hde.js'
const CONTAINER_ID = 'hde-container'
const DEFAULT_POSITION = 'bottom-right'
const WIDGET_WIDTH = 370
const WIDGET_HEIGHT = 480
const MOBILE_WIDGET_STYLE_ID = 'impulse-hde-mobile-style'
const MOBILE_CLOSE_BUTTON_CLASS = 'impulse-hde-mobile-close'
// Положение виджета проставляем на контейнер атрибутом, чтобы отступы из global.css
// не спорили с левым положением (см. #hde-container там же).
const POSITION_ATTRIBUTE = 'data-impulse-hde-position'
const OPEN_ATTRIBUTE = 'data-impulse-hde-open'
const TOUCH_ATTRIBUTE = 'data-impulse-hde-touch'

// Поля формы виджета ищем по плейсхолдерам: у виджета нет ни id, ни name.
const NAME_PLACEHOLDER = /^имя/i
const EMAIL_PLACEHOLDER = /^(э-?\s*почта|эл\.?\s*почта|e-?mail)/i

// Поля, которые уже заполнили: не перезаписываем то, что пользователь исправил вручную.
const filledInputs = new WeakSet()

// Приводим введённый адрес к чистому хосту: убираем протокол и хвостовые слэши
const normalizeHost = (value) =>
  (value || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '')

const getProfileFullName = (profile) => {
  const userProfile = profile?.userProfile || {}
  const directName =
    profile?.full_name ||
    profile?.fullName ||
    profile?.fio ||
    userProfile?.full_name ||
    userProfile?.fullName ||
    userProfile?.fio ||
    profile?.name

  if (directName) return String(directName).trim()

  return [
    profile?.last_name || profile?.lastName || userProfile?.last_name,
    profile?.first_name || profile?.firstName || userProfile?.first_name,
    profile?.middle_name || profile?.middleName || userProfile?.middle_name,
  ]
    .filter(Boolean)
    .join(' ')
    .trim()
}

const setInputValue = (input, value) => {
  const view = input.ownerDocument?.defaultView
  const nativeSetter =
    view &&
    Object.getOwnPropertyDescriptor(view.HTMLInputElement.prototype, 'value')
      ?.set

  // Значение ставим нативным сеттером: иначе Vue виджета не увидит изменение
  if (nativeSetter) nativeSetter.call(input, value)
  else input.value = value

  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))
  filledInputs.add(input)
}

const fillInputs = (root, placeholderMatcher, value) => {
  if (!value) return

  root.querySelectorAll('input[placeholder]').forEach((input) => {
    if (filledInputs.has(input)) return
    if (input.value.trim()) return
    if (!placeholderMatcher.test(input.placeholder.trim())) return

    setInputValue(input, value)
  })
}

const injectMobileWidgetStyles = (root) => {
  if (!root?.head || root.getElementById(MOBILE_WIDGET_STYLE_ID)) return

  const style = root.createElement('style')
  style.id = MOBILE_WIDGET_STYLE_ID
  style.textContent = `
    @media (max-width: 480px) {
      #widget-app.widget-app_mobile .container {
        height: 100%;
        min-height: 0;
        overflow: hidden;
      }

      #widget-app.widget-app_mobile .header {
        display: flex !important;
        flex: 0 0 auto;
        min-height: 56px;
        position: relative;
        z-index: 5;
      }

      #widget-app.widget-app_mobile .messages {
        flex: 1 1 auto;
        min-height: 0;
      }

      #widget-app.widget-app_mobile .widget-close,
      #widget-app.widget-app_mobile .widget-close_mobile {
        display: block !important;
        opacity: 1 !important;
        visibility: visible !important;
        position: absolute !important;
        top: 14px !important;
        right: 14px !important;
        z-index: 10 !important;
      }
    }
  `
  root.head.appendChild(style)
}

// Ищем только внутри разметки виджета: на страницах платформы есть свои поля
// с такими же плейсхолдерами, их трогать нельзя.
const getWidgetElements = () =>
  Array.from(
    document.querySelectorAll('[id^="hde-"], [class*="hde-chat"]'),
  ).filter((el) => el.id !== SCRIPT_ID)

// Форма виджета может рендериться как в самой странице, так и в iframe.
// Чужой домен закрыт браузером — тогда подставить данные с нашей стороны нельзя.
const getFormRoots = (elements) => {
  const roots = [...elements]

  elements.forEach((element) => {
    element.querySelectorAll('iframe').forEach((frame) => {
      try {
        if (frame.contentDocument) roots.push(frame.contentDocument)
      } catch (error) {
        // кросс-доменный iframe, доступа к его DOM нет
      }
    })
  })

  return roots
}

const setWidgetOpenState = () => {
  const container = document.getElementById(CONTAINER_ID)
  if (!container) return

  const chatContainer = document.getElementById('hde-chat-container')
  const rect = chatContainer?.getBoundingClientRect()
  const isOpen =
    Boolean(chatContainer) &&
    Boolean(rect) &&
    rect.width > 120 &&
    rect.height > 120
  const nextValue = isOpen ? '1' : '0'

  if (container.getAttribute(OPEN_ATTRIBUTE) !== nextValue) {
    container.setAttribute(OPEN_ATTRIBUTE, nextValue)
  }
}

const ensureMobileCloseButton = () => {
  const container = document.getElementById(CONTAINER_ID)
  if (!container) {
    return
  }

  const isTouchDevice =
    navigator.maxTouchPoints > 0 || 'ontouchstart' in window
  container.setAttribute(TOUCH_ATTRIBUTE, isTouchDevice ? '1' : '0')

  if (container.querySelector(`.${MOBILE_CLOSE_BUTTON_CLASS}`)) {
    return
  }

  const button = document.createElement('button')
  button.type = 'button'
  button.className = MOBILE_CLOSE_BUTTON_CLASS
  button.setAttribute('aria-label', 'Закрыть чат поддержки')
  button.setAttribute('title', 'Закрыть чат поддержки')
  button.textContent = '×'
  button.addEventListener('click', (event) => {
    event.preventDefault()
    event.stopPropagation()
    window.postMessage({ type: 'closeChatWindow' }, window.location.origin)
  })

  container.appendChild(button)
}

// Виджет чата поддержки HelpDeskEddy.
// Настраивается в «Настройки платформы → Интеграции → Интеграция с HelpDeskEddy».
// Скрипт добавляется на страницу только когда интеграция включена и задан data-host.
const HelpDeskEddyWidget = () => {
  const enabled =
    useSelector((store) => store.settings?.helpDeskEddyIntegrationEnabled) == '1'
  const host = useSelector((store) => store.settings?.helpDeskEddyHost)
  const position =
    useSelector((store) => store.settings?.helpDeskEddyPosition) ||
    DEFAULT_POSITION
  const hideForGuests =
    useSelector((store) => store.settings?.helpDeskEddyHideForGuests) == '1'

  const isAuth = useSelector(selectIsAuth)
  const profile = useSelector(selectProfile)

  const cleanedHost = normalizeHost(host)
  // Страницы авторизации, каталога и продления обучения открыты и без входа —
  // там виджет прячем, если так настроено.
  const hidden = hideForGuests && !isAuth
  const active = enabled && !!cleanedHost && !hidden

  const userName = getProfileFullName(profile)
  const userEmail = (profile?.email || '').trim()

  useEffect(() => {
    if (!active) return

    if (document.getElementById(SCRIPT_ID)) return

    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.id = SCRIPT_ID
    script.setAttribute('data-lang', 'ru')
    script.setAttribute('data-host', cleanedHost)
    script.setAttribute('data-position', position)
    script.setAttribute('data-width', WIDGET_WIDTH)
    script.setAttribute('data-height', WIDGET_HEIGHT)
    if (userName) script.setAttribute('data-user-name', userName)
    if (userEmail) script.setAttribute('data-user-email', userEmail)
    script.defer = true
    document.body.appendChild(script)

    return () => {
      // Убираем скрипт и контейнеры виджета, чтобы отключение/смена настроек
      // не оставляли старый виджет на странице (полностью применяется после перезагрузки).
      document.getElementById(SCRIPT_ID)?.remove()
      document
        .querySelectorAll('[id^="hde-"], [class*="hde-chat"]')
        .forEach((el) => {
          if (el.id !== SCRIPT_ID) el.remove()
        })
    }
  }, [active, cleanedHost, position, userName, userEmail])

  // Контейнер виджета появляется асинхронно, а форма — только когда её открыли,
  // поэтому ждём их через наблюдатель и подставляем имя с почтой из профиля.
  useEffect(() => {
    if (!active) return

    let frame = null

    const apply = () => {
      frame = null

      document
        .getElementById(CONTAINER_ID)
        ?.setAttribute(POSITION_ATTRIBUTE, position)
      ensureMobileCloseButton()
      setWidgetOpenState()
      setTimeout(setWidgetOpenState, 120)
      setTimeout(setWidgetOpenState, 350)

      getFormRoots(getWidgetElements()).forEach((root) => {
        injectMobileWidgetStyles(root)
        if (!userName && !userEmail) return

        fillInputs(root, NAME_PLACEHOLDER, userName)
        fillInputs(root, EMAIL_PLACEHOLDER, userEmail)
      })
    }

    // Мутаций в SPA много, поэтому склеиваем их в один проход на кадр.
    const scheduleApply = () => {
      if (frame !== null) return
      frame = requestAnimationFrame(apply)
    }

    apply()

    const observer = new MutationObserver(scheduleApply)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
      if (frame !== null) cancelAnimationFrame(frame)
    }
  }, [active, position, userName, userEmail])

  return null
}

export default HelpDeskEddyWidget
